import { useMemo } from 'react';
import { CM_PRIMARY_ID, SE_COMMITTEE_ID } from '../../utils/constants';

export const useAllCommitteesData = (
  coiData,
  candidateArray,
  committeeArray,
  sectionalCommitteesWithCmse,
  fellowsArray,
  nonAasRefereesArray,
) =>
  useMemo(() => {
    if (!coiData.length || !candidateArray.length || !committeeArray.length) {
      return { candidates: [], committeeMembers: [], committees: [] };
    }

    const uniqueCommitteeIds = [
      ...new Set(coiData.map((coi) => coi.attributes.sectionalCommitteeId)),
    ];

    const uniqueCandidateIds = [
      ...new Set(coiData.map((coi) => coi.attributes.candidateId)),
    ];

    const candidates = uniqueCandidateIds
      .map((candidateId) => {
        const candidate = candidateArray.find((c) => c.id === candidateId);
        return {
          id: candidateId,
          name: candidate
            ? `${candidate.attributes.name || ''} ${candidate.attributes.lastName || ''}`.trim()
            : `Candidate ${candidateId}`,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    const getMemberName = (member) => {
      let memberName = `User ${member.id}`;

      if (member.attributes.fellowId) {
        const fellow = fellowsArray.find(
          (f) => f.id === member.attributes.fellowId,
        );
        if (fellow) {
          memberName =
            `${fellow.attributes.name} ${fellow.attributes.lastName}`.trim();
        }
      } else if (member.attributes.nonAasRefereeId) {
        const referee = nonAasRefereesArray.find(
          (r) => r.id === member.attributes.nonAasRefereeId,
        );
        if (referee) {
          memberName =
            `${referee.attributes.name} ${referee.attributes.lastName}`.trim();
        }
      }

      return memberName;
    };

    const committees = [];
    const allCommitteeMembers = [];

    uniqueCommitteeIds.forEach((committeeId) => {
      let committeeData = committeeArray.find((c) => c.id === committeeId);

      if (committeeId === SE_COMMITTEE_ID) {
        committeeData = committeeArray.find((c) => c.id === CM_PRIMARY_ID);
      }

      if (!committeeData) return;

      const committeeMembers = [];

      if (committeeData?.attributes?.committeeChair?.data) {
        const chair = committeeData.attributes.committeeChair.data;
        const memberData = {
          id: chair.id,
          name: getMemberName(chair),
          isChair: true,
          committeeId,
        };
        committeeMembers.push(memberData);

        if (!allCommitteeMembers.find((m) => m.id === chair.id)) {
          allCommitteeMembers.push(memberData);
        }
      }

      if (committeeData?.attributes?.committeeMembers?.data) {
        committeeData.attributes.committeeMembers.data.forEach((member) => {
          if (!committeeMembers.find((m) => m.id === member.id)) {
            const memberData = {
              id: member.id,
              name: getMemberName(member),
              isChair: false,
              committeeId,
            };
            committeeMembers.push(memberData);

            if (!allCommitteeMembers.find((m) => m.id === member.id)) {
              allCommitteeMembers.push(memberData);
            }
          }
        });
      }

      const committeeCandidates = candidates.filter((candidate) =>
        coiData.some(
          (coi) =>
            coi.attributes.candidateId === candidate.id &&
            coi.attributes.sectionalCommitteeId === committeeId,
        ),
      );

      const sectionalCommitteeName = sectionalCommitteesWithCmse.find(
        (c) => c.id === committeeId,
      );

      const committeeName = sectionalCommitteeName?.attributes?.name || '';

      committees.push({
        id: committeeId,
        name: committeeName,
        candidates: committeeCandidates,
        committeeMembers,
      });
    });

    return {
      candidates,
      committeeMembers: allCommitteeMembers,
      committees,
    };
  }, [
    coiData,
    candidateArray,
    committeeArray,
    fellowsArray,
    nonAasRefereesArray,
  ]);
